import { useState } from 'react';
import { doc, setDoc, addDoc, collection, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { Pizzeria } from '../../types';
import Modal from '../Modal';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

async function uploadToFirebaseStorage(file: File): Promise<string> {
  try {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('Il file è troppo grande. Dimensione massima: 10MB');
    }

    // Crea il nome del file
    const timestamp = Date.now();
    const safeFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
    const finalFileName = `${timestamp}_${safeFileName}`;
    
    // Crea il riferimento allo storage con il bucket corretto
    const storageRef = ref(storage, `pizzerie/${finalFileName}`);

    // Upload del file
    const snapshot = await uploadBytes(storageRef, file);
    console.log('Upload completato:', snapshot.metadata.fullPath);

    // Ottieni l'URL di download
    const downloadURL = await getDownloadURL(storageRef);
    console.log('URL ottenuto:', downloadURL);

    return downloadURL;

  } catch (error) {
    console.error('Errore durante l\'upload:', error);
    throw error instanceof Error ? error : new Error('Errore sconosciuto durante l\'upload');
  }
}

interface PizzeriaFormProps {
  pizzeria: Pizzeria | null;
  onClose: () => void;
  userId: string | undefined;
}

export default function PizzeriaForm({ pizzeria, onClose, userId }: PizzeriaFormProps) {
  const [formData, setFormData] = useState({
    name: pizzeria?.name || '',
    description: pizzeria?.description || '',
    address: pizzeria?.address || '',
    city: pizzeria?.city || '',
    lat: pizzeria?.lat || '',
    lng: pizzeria?.lng || '',
    email: pizzeria?.email || '',
    phone: pizzeria?.phone || '',
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>(pizzeria?.imageUrl || '');
  const [fileError, setFileError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): boolean => {
    setFileError(null);

    if (!file.type.startsWith('image/')) {
      setFileError('Il file deve essere un\'immagine (PNG, JPG o GIF)');
      return false;
    }

    return true;
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (validateFile(file)) {
        setPhoto(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (validateFile(file)) {
        setPhoto(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      let imageUrl = pizzeria?.imageUrl || '';

      if (photo) {
        // Carica su Firebase Storage ma salva l'URL nel formato demo.pizzaveg.it
        imageUrl = await uploadToFirebaseStorage(photo);
        console.log('URL immagine salvato:', imageUrl);
      }

      const pizzeriaData = {
        ...formData,
        imageUrl, // Questo sarà nel formato https://demo.pizzaveg.it/user/img/nomefile
        updatedAt: serverTimestamp(),
        userId
      };

      if (pizzeria?.id) {
        await updateDoc(doc(db, 'pizzerie', pizzeria.id), pizzeriaData);
      } else {
        await addDoc(collection(db, 'pizzerie'), {
          ...pizzeriaData,
          createdAt: serverTimestamp()
        });
      }

      onClose();
    } catch (error) {
      console.error('Errore durante il salvataggio:', error);
      setError(error instanceof Error ? error.message : 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
        <h2 className="text-2xl font-bold mb-4">
          {pizzeria ? 'Modifica Pizzeria' : 'Nuova Pizzeria'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nome</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Telefono</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Città</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Indirizzo</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Descrizione</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Foto della Pizzeria
            </label>
            <div 
              className="flex justify-center items-center w-full"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <label 
                className={`w-full flex flex-col items-center px-4 py-6 bg-white rounded-lg border-2 
                  ${photoPreview ? 'border-green-200' : fileError ? 'border-red-200' : 'border-dashed border-gray-300'} 
                  cursor-pointer hover:bg-gray-50 transition-colors duration-200 ease-in-out
                  relative group`}
              >
                <div className="flex flex-col items-center justify-center">
                  {photoPreview ? (
                    <div className="relative">
                      <img 
                        src={photoPreview} 
                        alt="Preview" 
                        className="w-64 h-64 object-cover rounded-lg mb-4"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-lg transition-all duration-200 flex items-center justify-center">
                        <p className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          Clicca o trascina per cambiare
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Clicca per caricare</span> o trascina un'immagine
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG o GIF (max. 10MB)</p>
                    </>
                  )}
                </div>
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
              </label>
            </div>

            {fileError && (
              <p className="mt-2 text-sm text-red-600">
                {fileError}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Latitudine</label>
              <input
                type="number"
                step="any"
                value={formData.lat}
                onChange={(e) => setFormData(prev => ({ ...prev, lat: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Longitudine</label>
              <input
                type="number"
                step="any"
                value={formData.lng}
                onChange={(e) => setFormData(prev => ({ ...prev, lng: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pizzaveg focus:ring-pizzaveg"
                required
              />
            </div>
          </div>

          {error && (
            <div className="text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-pizzaveg hover:bg-pizzaveg-dark rounded-md"
              disabled={loading}
            >
              {loading ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}